import { Role } from 'src/@types/role';

type RbacTable = {
  [role in Role]: string[];
};

const actionAccessTable: RbacTable = {
  NuminarAdmin: ['*'],
  DTAdmin: [
    'data/tags/view',
    'data/tags/create',
    'data/exports/view',
    'data/exports/view_all',
    'data/uploads/view',
    'data/uploads/create',
    'data/models/view',
    'data/models/create',
    'crm/contacts/delete',
    'lists/create',
    'lists/delete',
    'lists/edit/*',
    'teammates/list',
    'teammates/invite/role=DatatrustOwner',
    'teammates/invite/role=DatatrustUser',
    'teammates/export',
    'settings/organization',
    'settings/admin_tools',
    'voter-search',
    'voter-search/contact-info',
  ],
  Owner: [
    'crm/contacts/delete',
    'lists/create',
    'lists/delete',
    'lists/edit/*',
    'projects/manage/type=*',
    'projects/create/type=*',
    // Deliberately outside the 'projects/create/type=' wildcard, so that holding every project
    // type does not by itself carry the partner enquiry. The enquiry commits spend with a media
    // partner, so it goes with 'settings/billing' rather than with the project types, which is why
    // ParentAdmin holds every type without it.
    'projects/digital-ctv-enquiry',
    'projects/delete/type=*',
    'projects/edit/type=*',
    'projects/select_all',
    'projects/assign',
    'projects/cost/view',
    'phoneNums',
    'messaging/surveys/edit',
    'messaging/survey/delete',
    'messaging/texts/view',
    'messaging/texts/edit',
    'messaging/text/delete',
    'messaging/voicemails/view',
    'messaging/voicemails/edit',
    'messaging/voicemail/delete',
    'messaging/conditional-survey/delete',
    'messaging/conditional-text/delete',
    'data/tags/view',
    'data/tags/create',
    'data/exports/view',
    'data/uploads/view',
    'data/uploads/create',
    'data/models/view',
    'data/models/create',
    'teammates/list',
    'teammates/invite/role=Admin',
    'teammates/invite/role=Staff',
    'teammates/invite/role=FieldOrganizer',
    'teammates/invite/role=Volunteer',
    'teammates/invite/role=Supporter',
    'teammates/export',
    'teammates/send-push-notification',
    'settings/organization',
    'settings/billing',
    'settings/billing/request-cancellation',
    'settings/relational_organizing',
    'settings/canvassing_qa_alerts',
    'teams/team-tab/view',
    'teams/team-tab/delete',
    'teams/team-tab/rename',
    'conversations',
    'campaigns/view',
    'campaigns/approve',
    'voter-search',
    'voter-search/contact-info',
  ],
  Admin: [
    'crm/contacts/delete',
    'lists/create',
    'lists/delete',
    'lists/edit/*',
    'projects/manage/type=*',
    'projects/create/type=*',
    // Deliberately outside the 'projects/create/type=' wildcard, and held alongside
    // 'settings/billing': the enquiry commits spend with a media partner.
    'projects/digital-ctv-enquiry',
    'projects/delete/type=*',
    'projects/edit/type=*',
    'projects/select_all',
    'projects/assign',
    'projects/cost/view',
    'phoneNums',
    'messaging/surveys/edit',
    'messaging/survey/delete',
    'messaging/texts/view',
    'messaging/texts/edit',
    'messaging/text/delete',
    'messaging/voicemails/view',
    'messaging/voicemails/edit',
    'messaging/voicemail/delete',
    'messaging/conditional-survey/delete',
    'messaging/conditional-text/delete',
    'data/tags/view',
    'data/tags/create',
    'data/exports/view',
    'data/uploads/view',
    'data/uploads/create',
    'data/models/view',
    'data/models/create',
    'teammates/list',
    'teammates/invite/role=Admin',
    'teammates/invite/role=Staff',
    'teammates/invite/role=FieldOrganizer',
    'teammates/invite/role=Volunteer',
    'teammates/invite/role=Supporter',
    'teammates/export',
    'teammates/send-push-notification',
    'settings/organization',
    'settings/billing',
    'settings/relational_organizing',
    'settings/canvassing_qa_alerts',
    'teams/team-tab/view',
    'teams/team-tab/delete',
    'teams/team-tab/rename',
    'conversations',
    'campaigns/view',
    'campaigns/approve',
    'voter-search',
    'voter-search/contact-info',
  ],
  ParentAdmin: [
    'crm/contacts/delete',
    'lists/create',
    'lists/delete',
    'lists/edit/*',
    'projects/manage/type=*',
    'projects/create/type=*',
    'projects/delete/type=*',
    'projects/edit/type=*',
    'projects/select_all',
    'projects/assign',
    'projects/cost/view',
    'phoneNums',
    'messaging/surveys/edit',
    'messaging/survey/delete',
    'messaging/texts/view',
    'messaging/texts/edit',
    'messaging/text/delete',
    'messaging/voicemails/view',
    'messaging/voicemails/edit',
    'messaging/voicemail/delete',
    'messaging/conditional-survey/delete',
    'messaging/conditional-text/delete',
    'data/tags/view',
    'data/tags/create',
    'data/exports/view',
    'data/uploads/view',
    'data/uploads/create',
    'data/models/view',
    'teammates/list',
    'teammates/invite/role=Admin',
    'teammates/invite/role=Staff',
    'teammates/invite/role=FieldOrganizer',
    'teammates/invite/role=Volunteer',
    'teammates/invite/role=Supporter',
    'teammates/export',
    'teammates/send-push-notification',
    'settings/organization',
    'settings/relational_organizing',
    'settings/canvassing_qa_alerts',
    'teams/team-tab/view',
    'teams/team-tab/delete',
    'teams/team-tab/rename',
    'conversations',
    'campaigns/view',
    'voter-search',
    'voter-search/contact-info',
  ],
  Staff: [
    'crm/contacts/delete',
    'lists/create',
    'lists/delete',
    'lists/edit/*',
    'projects/manage/type=*',
    'projects/assign',
    'projects/create/type=canvass',
    'projects/create/type=call',
    'projects/delete/type=*',
    'projects/edit/type=*',
    'projects/select_all',
    'projects/cost/view',
    'messaging/surveys/edit',
    'messaging/survey/delete',
    'messaging/texts/view',
    'messaging/texts/edit',
    'messaging/text/delete',
    'messaging/voicemails/view',
    'messaging/voicemails/edit',
    'messaging/voicemail/delete',
    'messaging/conditional-survey/delete',
    'messaging/conditional-text/delete',
    'teammates/list',
    'teammates/invite/role=FieldOrganizer',
    'teammates/invite/role=Volunteer',
    'teammates/invite/role=Supporter',
    'teammates/send-push-notification',
    'data/exports/view',
    'teams/team-tab/view',
    'conversations',
    'voter-search',
    'voter-search/contact-info',
  ],
  FieldOrganizer: [
    'lists/edit/geofences',
    'projects/create/type=canvass',
    'projects/delete/type=canvass',
    'projects/edit/type=canvass',
    'projects/assign',
    'projects/cost/view',
    'teammates/list',
    'teammates/invite/role=Volunteer',
    'teammates/invite/role=Supporter',
    'teams/team-tab/view',
    'voter-search',
    'voter-search/contact-info',
  ],
  Volunteer: ['voter-search'],
  Supporter: [],
  ManagedOutreachCoordinator: [],
  DatatrustOwner: [
    'data/tags/view',
    'data/tags/create',
    'data/models/view',
    'data/models/create',
    'data/exports/view',
    'data/uploads/view',
    'data/uploads/create',
    'crm/contacts/delete',
    'lists/create',
    'lists/delete',
    'lists/edit/*',
    'teammates/list',
    'teammates/invite/role=DatatrustUser',
    'teammates/export',
    'voter-search',
    'voter-search/contact-info',
  ],
  DatatrustUser: [
    'data/tags/view',
    'data/tags/create',
    'data/models/view',
    'data/models/create',
    'data/exports/view',
    'data/uploads/view',
    'data/uploads/create',
    'crm/contacts/delete',
    'lists/create',
    'lists/delete',
    'lists/edit/*',
    'teammates/list',
    'voter-search',
    'voter-search/contact-info',
  ],
};

const pageAccessTable: RbacTable = {
  NuminarAdmin: ['*'],
  DTAdmin: [
    '/settings',
    '/team',
    '/settings/organization',
    '/settings/organization/creation',
    '/data',
    '/lists',
    '/lists/*',
    '/data/tags',
    '/data/tags/*',
    '/data/models',
    '/data/models/*',
    '/data/upload',
    '/data/exports',
    '/crm/contacts',
    '/onboarding',
    '/onboarding/success',
    '/coming-soon',
    '/maintenance',
    'https://numinar.productlane.io/*',
    'https://intercom.help/*',
    '/500',
    '/403',
    '/404',
  ],
  Owner: [
    '/home',
    '/projects',
    '/reports',
    '/canvassing',
    '/settings',
    '/team',
    '/settings/organization',
    '/data',
    '/lists',
    '/lists/*',
    '/data/tags',
    '/data/tags/*',
    '/data/models',
    '/data/models/*',
    '/data/upload',
    '/data/exports',
    '/crm/contacts',
    '/messaging',
    '/messaging/surveys',
    '/messaging/surveys/*',
    '/messaging/conditional-surveys/*',
    '/messaging/texts',
    '/messaging/texts/*',
    '/messaging/conditional-texts/*',
    '/messaging/voicemails',
    '/messaging/voicemails/*',
    '/conversations',
    '/campaigns',
    'sign-up',
    'sign-up/success',
    'signup',
    'signup/success',
    'get-started',
    'get-started/success',
    '/coming-soon',
    '/maintenance',
    'https://numinar.productlane.io/*',
    'https://intercom.help/*',
    '/500',
    '/403',
    '/404',
  ],
  Admin: [
    // Org Admin not Numinar Admin
    '/home',
    '/projects',
    '/reports',
    '/canvassing',
    '/settings',
    '/team',
    '/settings/organization',
    '/data',
    '/lists',
    '/lists/*',
    '/data/tags',
    '/data/tags/*',
    '/data/models',
    '/data/models/*',
    '/data/upload',
    '/data/exports',
    '/crm/contacts',
    'sign-up',
    'sign-up/success',
    'signup',
    'signup/success',
    'get-started',
    'get-started/success',
    '/messaging',
    '/messaging/surveys',
    '/messaging/surveys/*',
    '/messaging/conditional-surveys/*',
    '/messaging/texts',
    '/messaging/texts/*',
    '/messaging/conditional-texts/*',
    '/messaging/voicemails',
    '/messaging/voicemails/*',
    '/conversations',
    '/campaigns',
    '/coming-soon',
    '/maintenance',
    'https://numinar.productlane.io/*',
    'https://intercom.help/*',
    '/500',
    '/403',
    '/404',
  ],
  ParentAdmin: [
    '/home',
    '/projects',
    '/reports',
    '/canvassing',
    '/settings',
    '/team',
    '/settings/organization',
    '/data',
    '/lists',
    '/lists/*',
    '/data/tags',
    '/data/tags/*',
    '/data/models',
    '/data/models/*',
    '/data/upload',
    '/data/exports',
    '/crm/contacts',
    '/messaging',
    '/messaging/surveys',
    '/messaging/surveys/*',
    '/messaging/conditional-surveys/*',
    '/messaging/texts',
    '/messaging/texts/*',
    '/messaging/conditional-texts/*',
    '/messaging/voicemails',
    '/messaging/voicemails/*',
    '/conversations',
    '/campaigns',
    'sign-up',
    'sign-up/success',
    'signup',
    'signup/success',
    'get-started',
    'get-started/success',
    '/coming-soon',
    '/maintenance',
    'https://numinar.productlane.io/*',
    'https://intercom.help/*',
    '/500',
    '/403',
    '/404',
  ],
  Staff: [
    '/home',
    '/projects',
    '/reports',
    '/canvassing',
    '/settings',
    '/team',
    '/data',
    '/lists',
    '/lists/*',
    '/data/exports',
    '/crm/contacts',
    'sign-up',
    'sign-up/success',
    'signup',
    'signup/success',
    'get-started',
    'get-started/success',
    '/messaging',
    '/messaging/surveys',
    '/messaging/surveys/*',
    '/messaging/conditional-surveys/*',
    '/messaging/texts',
    '/messaging/texts/*',
    '/messaging/conditional-texts/*',
    '/messaging/voicemails',
    '/messaging/voicemails/*',
    '/conversations',
    '/coming-soon',
    '/maintenance',
    'https://numinar.productlane.io/*',
    'https://intercom.help/*',
    '/500',
    '/403',
    '/404',
  ],
  FieldOrganizer: [
    '/projects',
    '/reports',
    '/canvassing',
    '/settings',
    '/team',
    '/lists',
    '/lists/*',
    'sign-up',
    'sign-up/success',
    'signup',
    'signup/success',
    'get-started',
    'get-started/success',
    '/messaging',
    '/messaging/surveys',
    '/messaging/surveys/*',
    '/messaging/conditional-surveys/*',
    '/coming-soon',
    '/maintenance',
    'https://numinar.productlane.io/*',
    'https://intercom.help/*',
    '/500',
    '/403',
    '/404',
  ],
  Volunteer: [
    '/projects',
    '/settings',
    'sign-up',
    'sign-up/success',
    'signup',
    'signup/success',
    'get-started',
    'get-started/success',
    '/coming-soon',
    '/maintenance',
    'https://numinar.productlane.io/*',
    'https://intercom.help/*',
    '/500',
    '/403',
    '/404',
  ],
  Supporter: [
    '/projects',
    '/settings',
    'sign-up',
    'sign-up/success',
    'signup',
    'signup/success',
    'get-started',
    'get-started/success',
    '/coming-soon',
    '/maintenance',
    'https://numinar.productlane.io/*',
    'https://intercom.help/*',
    '/500',
    '/403',
    '/404',
  ],
  ManagedOutreachCoordinator: [
    '/projects',
    '/settings',
    'sign-up',
    'sign-up/success',
    'signup',
    'signup/success',
    'get-started',
    'get-started/success',
    '/coming-soon',
    '/maintenance',
    'https://numinar.productlane.io/*',
    'https://intercom.help/*',
    '/500',
    '/403',
    '/404',
  ],
  DatatrustOwner: [
    '/settings',
    '/team',
    '/data',
    '/lists',
    '/lists/*',
    '/data/tags',
    '/data/tags/*',
    '/data/upload',
    '/data/exports',
    '/crm/contacts',
    '/coming-soon',
    '/maintenance',
    'https://numinar.productlane.io/*',
    'https://intercom.help/*',
    '/500',
    '/403',
    '/404',
  ],
  DatatrustUser: [
    '/settings',
    '/team',
    '/data',
    '/lists',
    '/lists/*',
    '/data/tags',
    '/data/tags/*',
    '/data/upload',
    '/data/exports',
    '/crm/contacts',
    '/coming-soon',
    '/maintenance',
    'https://numinar.productlane.io/*',
    'https://intercom.help/*',
    '/500',
    '/403',
    '/404',
  ],
};

// const subscriptionAccessTable = {
//   // TODO: Implement subscription fine-grained feature access 4/14/23
// };

export const roleHasPageAccess = (path: string, role: Role | undefined): boolean => {
  if (!role) return false;
  return pageAccessTable[role].some((pattern) => {
    if (pattern === '*') return true; // Numinar Admins
    if (pattern.endsWith('/*')) {
      const parentPath = pattern.slice(0, -2);
      return path.startsWith(parentPath);
    }
    return path === pattern;
  });
};

export const roleHasPermission = (
  action: string,
  role: Role | undefined,
  options?: { any?: boolean }
): boolean => {
  if (!role) return false;
  const { any = false } = options || {};
  return actionAccessTable[role].some((pattern) => {
    if (pattern === '*') return true; // Numinar Admins
    if (pattern.endsWith('*')) {
      const parentAction = pattern.slice(0, -1);
      return action.startsWith(parentAction);
    }
    if (any) return pattern.startsWith(action);
    return action === pattern;
  });
};

export const isHighRole = (role: Role | undefined): boolean => {
  if (!role) return false;
  return [Role.NuminarAdmin, Role.Owner, Role.OrgAdmin, Role.ParentAdmin].includes(role);
};
